import React, { useEffect, useRef, useState } from 'react';

function CorporateSectionLayout({ title, eyebrow = 'PT Len Telekomunikasi Indonesia', items = [] }) {
    const [activeSection, setActiveSection] = useState(items[0]?.id || '');
    const [compactNavVisible, setCompactNavVisible] = useState(true);
    const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);

    useEffect(() => {
        if (!items.length) return undefined;

        const sections = items
            .map((item) => document.getElementById(item.id))
            .filter(Boolean);

        if (!sections.length || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleEntries = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                if (visibleEntries[0]) {
                    setActiveSection(visibleEntries[0].target.id);
                }
            },
            {
                rootMargin: '-20% 0px -62% 0px',
                threshold: [0.05, 0.2, 0.4],
            }
        );

        sections.forEach((section) => observer.observe(section));
        return () => observer.disconnect();
    }, [items]);

    useEffect(() => {
        if (items.length <= 1) return undefined;

        let ticking = false;

        const updateVisibility = () => {
            const currentY = window.scrollY;
            const delta = currentY - lastScrollY.current;

            // Keep the compact navigation visible near the top of the page.
            if (currentY < 180) {
                setCompactNavVisible(true);
            } else if (delta > 8) {
                // Reading downward: move the compact navigation out of the way.
                setCompactNavVisible(false);
            } else if (delta < -8) {
                // The user is looking for navigation again.
                setCompactNavVisible(true);
            }

            lastScrollY.current = currentY;
            ticking = false;
        };

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateVisibility);
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [items.length]);

    const scrollToSection = (id) => {
        const target = document.getElementById(id);
        if (!target) return;

        const navbarHeight = 96;
        const compactNavigationHeight = window.innerWidth < 1280 ? 72 : 0;
        const top = target.getBoundingClientRect().top + window.pageYOffset - navbarHeight - compactNavigationHeight;

        window.scrollTo({
            top,
            behavior: 'smooth',
        });
    };

    const NavButton = ({ item, compact = false }) => {
        const isActive = activeSection === item.id;

        return (
            <button
                type="button"
                onClick={() => scrollToSection(item.id)}
                className={`group min-w-0 rounded-2xl border text-left transition-all duration-200 ${
                    compact
                        ? 'flex min-h-[48px] w-full items-center justify-center gap-2 px-2.5 py-2 sm:min-h-[52px] sm:px-3'
                        : 'flex w-full items-center gap-3 px-4 py-3.5'
                } ${
                    isActive
                        ? 'border-blue-400/30 bg-blue-500/15 text-white shadow-lg shadow-blue-950/20'
                        : 'border-transparent text-slate-300 hover:border-white/10 hover:bg-white/[0.06] hover:text-white'
                }`}
                aria-current={isActive ? 'page' : undefined}
            >
                <span
                    className={`flex flex-none items-center justify-center rounded-xl font-bold transition ${
                        compact ? 'h-7 w-7 text-[9px] sm:h-8 sm:w-8 sm:text-[10px]' : 'h-9 w-9 text-[11px]'
                    } ${
                        isActive
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/[0.07] text-slate-400 group-hover:text-blue-200'
                    }`}
                >
                    {item.number}
                </span>
                <span
                    className={`${compact ? 'text-[10px] leading-3.5 sm:text-xs sm:leading-4' : 'text-sm'} min-w-0 break-words font-semibold`}
                >
                    {item.label}
                </span>
                {!compact && (
                    <span
                        className={`ml-auto h-1.5 w-1.5 flex-none rounded-full transition ${
                            isActive
                                ? 'bg-blue-400 shadow-[0_0_12px_rgba(96,165,250,0.9)]'
                                : 'bg-slate-700'
                        }`}
                    />
                )}
            </button>
        );
    };

    return (
        <div className="bg-slate-50">
            {items.length > 1 && (
                <div
                    className={`sticky top-20 z-40 border-y border-slate-800/80 bg-slate-950/95 px-2.5 py-2 shadow-lg shadow-slate-950/10 backdrop-blur-xl transition-all duration-300 ease-out xl:hidden ${
                        compactNavVisible
                            ? 'translate-y-0 opacity-100'
                            : 'pointer-events-none -translate-y-[calc(100%+5rem)] opacity-0'
                    }`}
                >
                    <div
                        className={`mx-auto grid max-w-5xl gap-1.5 ${
                            items.length === 2 ? 'grid-cols-2' : 'grid-cols-3'
                        }`}
                    >
                        {items.map((item) => (
                            <NavButton key={item.id} item={item} compact />
                        ))}
                    </div>
                </div>
            )}

            <div
                className={`container mx-auto max-w-[1480px] px-4 py-7 sm:px-6 sm:py-9 lg:px-8 xl:py-12 ${
                    items.length > 1
                        ? 'xl:grid xl:grid-cols-[280px_minmax(0,1fr)] xl:items-stretch xl:gap-10 2xl:gap-14'
                        : ''
                }`}
            >
                {items.length > 1 && (
                    <aside className="hidden xl:block xl:self-stretch">
                        <div className="sticky top-24">
                            <div className="flex flex-col rounded-[28px] border border-slate-800/70 bg-gradient-to-b from-slate-950 via-[#0a192b] to-[#071423] p-3 shadow-[0_24px_65px_rgba(2,6,23,0.24)]">
                                <div className="px-4 pb-5 pt-4">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-300">
                                        {eyebrow}
                                    </span>
                                    <h2 className="mt-1 text-xl font-bold text-white">{title}</h2>
                                </div>

                                <div className="space-y-1.5">
                                    {items.map((item) => (
                                        <NavButton key={item.id} item={item} />
                                    ))}
                                </div>

                                <div className="mx-4 mt-5 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                                <div className="px-4 pb-3 pt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                                    Len Telko
                                </div>
                            </div>
                        </div>
                    </aside>
                )}

                <main className="min-w-0 space-y-8 xl:space-y-10">
                    {items.map((item) => (
                        <section key={item.id} id={item.id} className="scroll-mt-40 xl:scroll-mt-28">
                            {item.surface === false ? (
                                item.content
                            ) : (
                                <div className="lti-content-surface overflow-hidden rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_55px_rgba(15,23,42,0.07)] sm:p-7 lg:p-9">
                                    {item.content}
                                </div>
                            )}
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );
}

export default CorporateSectionLayout;
